<template>
  <Cobrand :affiliates="true">
    <LazySignModal v-if="showLegalModal" />
    <MarketplaceNav />
    <div :style="appStyleProps"
         data-smooth-scroll="main"
         class="marketplace-body-footer-container d-flex flex-column vh-100 overflow-x-hidden"
         :class="{'customized-affiliate': !!affiliateCustomizations}">
      <div class="d-flex flex-lg-grow-1 align-items-center align-items-lg-start flex-column mb-auto">
        <div class="px-5 px-lg-0 py-lg-0 marketplace-page"
             :class="{'customized-affiliate': !!affiliateCustomizations}">
          <slot />
          <div class="pt-4" v-if="currentApp.type === 'sba' && !isIntelligentLending">
            <p class="type-500">
              Not ready to provide the documentation required for an SBA Loan?
              <br />
              <a class="modal-link" @click="openModal">
                Return to the borrower dashboard.
              </a>
            </p>
            <PortalReturnModal
              :showModal="showModal"
              @close="showModal = false"
            />
          </div>
        </div>
      </div>
      <div class="d-flex align-content-between flex-column mt-auto">
        <MarketplaceFooter class="mb-3" />
      </div>
    </div>
    <ChatBot v-if="authUser" />
  </Cobrand>
</template>

<script>
import { LazySignModal } from '#components'
import { mapState } from 'pinia'
import { useAffiliateCustomizationStore } from '~/store/affiliate-customization'
import { useBorrowerStore } from '~/store/borrower'
import { useDocumentsStore } from '~/store/documents'
import { useProgressStore } from '~/store/progress'
import { useRootStore } from '~/store/root'
import { useUserStore } from '~/store/user'
import ChatBot from '~/components/ChatBot'
import Cobrand from '~/components/Cobrand'
import MarketplaceFooter from '~/components/marketplace/MarketplaceFooter'
import MarketplaceNav from '~/components/marketplace/MarketplaceNav'
import PortalReturnModal from '~/components/portal/sba/PortalReturnModal.vue'
import SmoothScroll from '~/libs/smooth-scroll'

export default {
  data() {
    return {
      showModal: false
    }
  },
  async setup() {
    const rootStore = useRootStore()
    const affiliateCustomizationsStore = useAffiliateCustomizationStore()
    const borrowerStore = useBorrowerStore()
    const { showLegalModal } = storeToRefs(borrowerStore)

    const isIntelligentLending = affiliateCustomizationsStore.isIntelligentLendingAffiliate

    rootStore.setPageTitle(affiliateCustomizationsStore.headTitlePrefix + ' - SBA Application')

    rootStore.setLendioFavicons()

    useHead({
      title: rootStore.pageTitle,
      link: rootStore.favicons,
    })

    return {
      isIntelligentLending,
      showLegalModal,
      userStore: useUserStore(),
      documentsStore: useDocumentsStore(),
      progressStore: useProgressStore(),
    }
  },
  components: {
    ChatBot,
    Cobrand,
    LazySignModal,
    PortalReturnModal,
    MarketplaceFooter,
    MarketplaceNav,
  },
  watch: {
    '$route.path' () {
      SmoothScroll.goTo(0)
      SmoothScroll.scrollElementsTop('[data-smooth-scroll="main"]')
    },
  },
  computed: {
    ...mapState(useProgressStore, ['currentApp']),
    ...mapState(useRootStore, {
      authUser: (store) => store.authUser
    }),
    ...mapState(useAffiliateCustomizationStore, [
      'appStyleProps',
      'affiliateCustomizations',
    ]),
  },
  async mounted() {
    await this.progressStore.fetchProgress()
    await this.documentsStore.getDocuments()
    await this.userStore.loadFreshLogin()
  },
  methods: {
    openModal() {
      this.showModal = true
    }
  }
}
</script>

<style lang="scss">

.marketplace-body-footer-container {
  @include media-breakpoint-up(lg) {
    padding-left: 400px;
  }

  &.customized-affiliate {
    background: $app-body-background-color;
  }
}
.marketplace-page {
  width: 100%;
  @include media-breakpoint-down(lg) {
    max-width: 365px;
    margin-top: 90px;
  }
  @include media-breakpoint-up(lg) {
    width: 600px;
    margin-top: calc(90px + var(--offset-top, 0px)); /* Spaced to match .sidebar-nav in MarketplaceSidebar minus back btn and 16 margin */
  }
  .no-value-select {
    color: $type-400;
  }

  &.customized-affiliate {
    @include affiliateCustomizedApp();
  }
}

.no-back-button-padding {
  padding-top: 58px; /* When a back button is included it takes up 58px */
}

.marketplace-header {
  :not(h3) {
    color: $type-500;
  }
}

.marketplace-content {
  > * {
    margin-bottom: 24px;
  }
}

.marketplace-content > :last-child {
  margin-bottom: 32px !important;
}

.marketplace-content > *:not(.marketplace-content-wide) {
  max-width: 360px;
}

.marketplace-content-wide {
  @include media-breakpoint-up(lg) {
    width: 600px;
  }
}
</style>
